import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCase } from '../../services/caseServices';
import { putPjudMonitoringClientOption } from 'services/detectionMonitoringServices';
import { fetchServicesByBusinessLine } from 'services/contractGeneratorService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { legalCasesI18n } from './i18n';

const NewCaseModal = ({
  DesignSystemComponents,
  show,
  onClose,
  clients = [],
  lawyers = [],
  courts = [],
  createInfo,
  userId,
  sponsors,
  onSearchLawyers,
  onSearchClients,
  fromMonitoring = false,
  monitoringId = null,
  preselectedClient = null,
  preselectedRol = null,
  onAfterCreate = null,
}) => {
  const { language } = useLanguage();
  const legalCasesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: legalCasesI18n });

  const { Modal, Input, Card, ComboBox } = DesignSystemComponents;

  const [step, setStep] = useState(1);

  const [selectedClient, setSelectedClient] = useState(null);
  const [services, setServices] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const clientOptions = clients.map((c) => ({
    label: `${c.name} (${c.identifier})`,
    value: c.id,
    identifier: c.identifier,
  }));

  const [selectedUser, setSelectedUser] = useState(null);

  const normalizeSingleSelection = (value) => {
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
  };

  const [form, setForm] = useState({
    title: '',
    rol: '',
    tribunal: null,
    businessAreaId: null,
    detailType: null,
    businessLine: null,
    procedure: null,
    serviceId: null,
    sponsorId: null,
    assignedUserId: null,
  });

  const lawyersActives = lawyers.filter((lawyer) => lawyer.active === true);

  const userOptions = lawyersActives.map((u) => ({
    label: u.name,
    value: u.id,
  }));

  const sponsorOptions = sponsors.map((u) => ({
    label: u.name,
    value: u.id,
  }));

  const tribunalOptions = courts
    .filter((x) => x.value !== 0)
    .map((court) => ({
      label: court.label,
      value: court.value,
    }));

  const groupOptions =
    createInfo?.businessArea
      ?.filter((u) => u.id !== 2 && u.id !== 11)
      .map((x) => ({
        label: x.name,
        value: x.id,
      })) ?? [];

  const detailOptions =
    createInfo?.caseTypesDetail?.map((x) => ({
      label: x.description,
      value: x.id,
    })) ?? [];

  const businessLineOptions =
    createInfo?.businessLine?.map((x) => ({ label: x.name, value: x.id })) ??
    [];

  const procedureOptions =
    createInfo?.procesess?.map((x) => ({
      label: x.description,
      value: x.id,
    })) ?? [];

  useEffect(() => {
    legalCasesI18n.changeLanguage(legalCasesLanguage);
  }, [legalCasesLanguage]);

  const isStep1Valid = !!selectedClient;
  const isStep2Valid = !!selectedUser;
  const isStep3Valid =
    form.title &&
    form.rol &&
    form.tribunal &&
    form.businessAreaId &&
    form.detailType &&
    form.businessLine &&
    form.procedure;

  const resolveSelectedClientId = () => {
    const directId =
      selectedClient?.id ??
      selectedClient?.clientId ??
      selectedClient?.backendId ??
      selectedClient?.idClient ??
      selectedClient?.value ??
      null;
    const numericDirectId = Number(directId);
    if (Number.isFinite(numericDirectId) && numericDirectId > 0) {
      return numericDirectId;
    }

    const selectedIdentifier = selectedClient?.identifier || selectedClient?.rut || '';
    const selectedLabel = selectedClient?.label || '';

    const matchedByIdentifier = clientOptions.find(
      (option) =>
        selectedIdentifier &&
        String(option.identifier || '').trim() === String(selectedIdentifier).trim()
    );
    if (matchedByIdentifier) {
      const matchedId = Number(matchedByIdentifier.value);
      if (Number.isFinite(matchedId) && matchedId > 0) return matchedId;
    }

    const matchedByLabel = clientOptions.find(
      (option) =>
        selectedLabel &&
        String(option.label || '').trim() === String(selectedLabel).trim()
    );
    if (matchedByLabel) {
      const matchedId = Number(matchedByLabel.value);
      if (Number.isFinite(matchedId) && matchedId > 0) return matchedId;
    }

    const matchedByPropsClient = (clients || []).find((client) => {
      const clientIdentifier = String(client?.identifier || '').trim();
      const clientName = String(client?.name || '').trim();
      return (
        (selectedIdentifier && clientIdentifier === String(selectedIdentifier).trim()) ||
        (selectedLabel && selectedLabel.includes(clientName))
      );
    });

    if (matchedByPropsClient) {
      const matchedId = Number(
        matchedByPropsClient?.id ??
          matchedByPropsClient?.clientId ??
          matchedByPropsClient?.backendId ??
          matchedByPropsClient?.idClient
      );
      if (Number.isFinite(matchedId) && matchedId > 0) return matchedId;
    }

    return null;
  };

  const handleNext = () => {
    if (step === 1 && !isStep1Valid) return;
    if (step === 2 && !isStep2Valid) return;
    setStep(step + 1);
  };

  const handlePrev = () => {
    if (step === 1) return;
    setStep(step - 1);
  };

  const handleCreateCase = async () => {
    if (!isStep3Valid || isSaving) return;

    setIsSaving(true);

    try {
      const resolvedClientId = resolveSelectedClientId();
      if (!resolvedClientId) {
        console.warn('Invalid client selected for case creation', selectedClient);
        setIsSaving(false);
        return;
      }

      if (fromMonitoring && !selectedClient?.value) {
        console.warn('Monitored client does not have a valid ID');
        setIsSaving(false);
        return;
      }

      const payload = {
        code: form.rol,
        title: form.title,
        clientId: resolvedClientId,
        businessAreaId: form.businessAreaId?.value,
        caseTypeDetailId: form.detailType?.value,
        tribunalId: form.tribunal?.value,
        processId: form.procedure?.value,
        lineaNegocioId: form.businessLine?.value,
        sponsorId: form.sponsorId?.value,
        assignedUserId: selectedUser.value,
        serviceId: form.serviceId?.value,
        userId,
        creatorId: userId,
      };

      await createCase(payload);

      if (fromMonitoring && monitoringId) {
        await putPjudMonitoringClientOption.putMonitoringOption(
          monitoringId,
          12,
          userId
        );
      }

      if (onAfterCreate) onAfterCreate(fromMonitoring);

      onClose();
    } catch (error) {
      console.error('Error creating case:', error);
      setIsSaving(false);
    }
  };

  const fetchBusinessLine = async (businessLine) => {
    try {
      const resp = await fetchServicesByBusinessLine(businessLine.value);
      setServices(resp);
    } catch (error) {
      console.error('Error fetching services: ', error);
    }
  };

  useEffect(() => {
    if (!show) return;

    setStep(1);
    setSelectedClient(null);
    setSelectedUser(null);
    setForm({
      title: '',
      rol: '',
      tribunal: null,
      businessAreaId: null,
      detailType: null,
      businessLine: null,
      procedure: null,
      serviceId: null,
      sponsorId: null,
      assignedUserId: null,
    });
  }, [show]);

  useEffect(() => {
    if (!show) {
      setSelectedClient(null);
      setSelectedUser(null);
      setForm({
        title: '',
        rol: '',
        tribunal: null,
        businessAreaId: null,
        detailType: null,
        businessLine: null,
        procedure: null,
        serviceId: null,
        sponsorId: null,
        assignedUserId: null,
      });
      return;
    }

    setStep(1);
    if (preselectedClient?.value != null) {
      // Set preselectedClient immediately — ensure it has id for downstream lookups
      setSelectedClient({
        ...preselectedClient,
        id: preselectedClient.value,
        clientId: preselectedClient.value,
      });
    } else {
      setSelectedClient(null);
    }

    setSelectedUser(null);

    if (fromMonitoring && preselectedRol) {
      setForm((prev) => ({
        ...prev,
        rol: preselectedRol,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        rol: '',
      }));
    }
  }, [show, fromMonitoring, preselectedClient, preselectedRol, clients]);

  useEffect(() => {
    if (show && fromMonitoring && preselectedRol) {
      setForm((f) => ({ ...f, rol: preselectedRol }));
    }
  }, [show, fromMonitoring, preselectedRol]);

  return (
    <Modal
      title={t('legalCases_modal_title')}
      show={show}
      onClose={onClose}
      secondaryBtnTxt={
        step === 1
          ? t('legalCases_modal_btn_cancel')
          : t('legalCases_modal_btn_previous')
      }
      secondaryBtnAction={step === 1 ? onClose : handlePrev}
      primaryBtnTxt={
        step === 3
          ? isSaving
            ? t('legalCases_modal_btn_saving')
            : t('legalCases_modal_btn_create')
          : t('legalCases_modal_btn_next')
      }
      primaryBtnAction={step === 3 ? handleCreateCase : handleNext}
      primaryBtnDisabled={
        isSaving ||
        (step === 1 && !isStep1Valid) ||
        (step === 2 && !isStep2Valid) ||
        (step === 3 && !isStep3Valid)
      }
    >
      {step === 1 && (
        <div>
          <ComboBox
            label={t('legalCases_modal_step1_clientLabel')}
            placeholder={t('legalCases_modal_step1_clientPlaceholder')}
            options={clientOptions}
            value={selectedClient}
            onChange={
              !fromMonitoring
                ? (value) => setSelectedClient(normalizeSingleSelection(value))
                : undefined
            }
            onInputChange={(text) => {
              if (!fromMonitoring && onSearchClients) {
                onSearchClients(text);
              }
            }}
            isDisabled={fromMonitoring}
          />

          {selectedClient && (
            <Card className='p-3 mt-3'>
              <h6 className='fw-semibold m-0'>
                {t('legalCases_modal_step1_selectedClient')}
              </h6>
              <p className='m-0'>{selectedClient.label}</p>
            </Card>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <ComboBox
            label={t('legalCases_modal_step2_userLabel')}
            placeholder={t('legalCases_modal_step2_userPlaceholder')}
            options={userOptions}
            value={selectedUser}
            onChange={(value) => setSelectedUser(normalizeSingleSelection(value))}
            onInputChange={(text) => {
              if (onSearchLawyers) {
                onSearchLawyers(text);
              }
            }}
          />

          {selectedUser && (
            <Card className='p-3 mt-3'>
              <h6 className='fw-semibold m-0'>
                {t('legalCases_modal_step2_selectedUser')}
              </h6>
              <p className='m-0'>{selectedUser.label}</p>
            </Card>
          )}
        </div>
      )}

      {step === 3 && (
        <div>
          <Input
            label={t('legalCases_modal_field_title_label')}
            placeholder={t('legalCases_modal_field_title_placeholder')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />

          <Input
            label={t('legalCases_modal_field_role_label')}
            placeholder={t('legalCases_modal_field_role_placeholder')}
            value={form.rol}
            onChange={(e) => {
              if (!fromMonitoring) {
                setForm({ ...form, rol: e.target.value });
              }
            }}
            readOnly={fromMonitoring}
          />

          <ComboBox
            label={t('legalCases_modal_field_court_label')}
            placeholder={t('legalCases_modal_field_court_placeholder')}
            options={tribunalOptions}
            value={form.tribunal}
            onChange={(value) => setForm({ ...form, tribunal: value })}
            className='space-like-input'
          />

          <ComboBox
            label={t('legalCases_modal_field_area_label')}
            placeholder={t('legalCases_modal_field_area_placeholder')}
            options={groupOptions}
            value={form.businessAreaId}
            onChange={(value) => setForm({ ...form, businessAreaId: value })}
            className='space-like-input'
          />

          <ComboBox
            label={t('legalCases_modal_field_caseDetail_label')}
            placeholder={t('legalCases_modal_field_caseDetail_placeholder')}
            options={detailOptions}
            value={form.detailType}
            onChange={(value) => setForm({ ...form, detailType: value })}
            className='space-like-input'
          />

          <ComboBox
            label={t('legalCases_modal_field_sponsor_label')}
            placeholder={t('legalCases_modal_field_sponsor_placeholder')}
            options={sponsorOptions}
            value={form.sponsorId}
            onChange={async (value) => setForm({ ...form, sponsorId: value })}
            className='space-like-input'
          />

          <ComboBox
            label={t('legalCases_modal_field_businessLine_label')}
            placeholder={t('legalCases_modal_field_businessLine_placeholder')}
            options={businessLineOptions}
            value={form.businessLine}
            onChange={async (value) => {
              const normalized = normalizeSingleSelection(value);
              setForm({ ...form, businessLine: normalized });
              if (normalized?.value) {
                await fetchBusinessLine(normalized);
              } else {
                setServices([]);
              }
            }}
            className='space-like-input'
          />

          <ComboBox
            label={t('legalCases_modal_field_service_label')}
            placeholder={t('legalCases_modal_field_service_placeholder')}
            options={services}
            value={form.serviceId}
            onChange={(value) => setForm({ ...form, serviceId: value })}
            className='space-like-input'
          />

          <ComboBox
            label={t('legalCases_modal_field_procedure_label')}
            placeholder={t('legalCases_modal_field_procedure_placeholder')}
            options={procedureOptions}
            value={form.procedure}
            onChange={(value) =>
              setForm({ ...form, procedure: normalizeSingleSelection(value) })
            }
          />
        </div>
      )}
    </Modal>
  );
};

export default NewCaseModal;
